import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playNotificationChime } from "@/lib/notificationSound";

/**
 * Navbar icon + unread count badge — mirrors NotificationBell's look.
 * Polls /api/conversations every 15 s for total_unread.
 * Plays a chime when the unread count *rises* (new message), not on
 * the initial mount and not when read-marking drops it.
 */
export default function MessagesNavButton() {
  const [unread, setUnread] = useState(0);
  const { user } = useAuth();
  const lastSeen = useRef(null);
  const soundOn = user?.notification_sound !== false;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/conversations");
        const next = data.total_unread || 0;
        if (lastSeen.current !== null && next > lastSeen.current && soundOn) {
          playNotificationChime();
        }
        lastSeen.current = next;
        setUnread(next);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [soundOn]);

  return (
    <Link
      to="/messages"
      className="relative h-9 w-9 rounded-md hover:bg-slate-100 flex items-center justify-center transition-colors"
      title="Messages"
      data-testid="nav-messages-btn"
    >
      <MessageSquare className="h-5 w-5 text-navy"/>
      {unread > 0 && (
        <span
          className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-teal text-white text-[10px] font-bold flex items-center justify-center"
          data-testid="nav-messages-badge"
        >
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Link>
  );
}
